/**
 * Order management page with filtering and export.
 */

import { useEffect, useState } from 'react'
import { api, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DataTable, Column } from '@/components/DataTable'
import { formatCurrency, formatDateTime, getStatusConfig } from '@/lib/formatters'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'

interface Order {
  id: number
  order_no: string
  amount: number
  currency: string
  status: string
  payment_channel: string | null
  customer_email: string | null
  created_at: string
  paid_at: string | null
}

interface OrderFilters {
  status: string
  date_from: string
  date_to: string
}

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<OrderFilters>({
    status: '',
    date_from: '',
    date_to: '',
  })
  const { toast } = useToast()

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const params: Record<string, string> = {}
      if (filters.status && filters.status !== 'all') params.status = filters.status
      if (filters.date_from) params.date_from = filters.date_from
      if (filters.date_to) params.date_to = filters.date_to

      const response = await api.get<Order[]>('/orders', params)
      setOrders(response.data)
      setError(null)
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message)
      } else {
        setError('Failed to fetch orders')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  const handleFilterApply = () => {
    fetchOrders()
  }

  const handleExportCSV = () => {
    if (orders.length === 0) {
      toast({ title: 'No data', description: 'No orders to export', variant: 'destructive' })
      return
    }

    // Generate CSV content
    const headers = ['Order No', 'Amount', 'Currency', 'Status', 'Payment Channel', 'Customer Email', 'Created At', 'Paid At']
    const rows = orders.map((order) => [
      order.order_no,
      (order.amount / 100).toFixed(2),
      order.currency,
      order.status,
      order.payment_channel || '',
      order.customer_email || '',
      order.created_at,
      order.paid_at || '',
    ])

    const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `orders_${new Date().toISOString().split('T')[0]}.csv`
    link.click()

    toast({ title: 'Export complete', description: `Exported ${orders.length} orders`, variant: 'success' })
  }

  const columns: Column<Order>[] = [
    {
      key: 'order_no',
      header: 'Order No',
      sortable: true,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (order) => formatCurrency(order.amount, order.currency),
    },
    {
      key: 'status',
      header: 'Status',
      render: (order) => {
        const config = getStatusConfig(order.status)
        return <Badge className={config.className}>{config.label}</Badge>
      },
    },
    {
      key: 'payment_channel',
      header: 'Channel',
      render: (order) => order.payment_channel || '-',
    },
    {
      key: 'customer_email',
      header: 'Customer',
      render: (order) => order.customer_email || '-',
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (order) => formatDateTime(order.created_at),
    },
  ]

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Orders</h1>
          <p className="text-muted-foreground mt-2">
            View and manage commerce (product) orders. Payment orders and refunds live under Payments.
          </p>
        </div>
        <Button onClick={handleExportCSV} variant="outline">
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleFilterApply} className="w-full">
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="mb-4 border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchOrders}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            data={orders}
            columns={columns}
            keyField="id"
            searchPlaceholder="Search by order no or email..."
            searchFields={['order_no', 'customer_email']}
            isLoading={loading}
            emptyMessage="No orders found."
          />
        </CardContent>
      </Card>
    </div>
  )
}
